import { $Props } from '@Ravnur-Inc/core/typings/tsx';
import _curry2 from '@Ravnur-Inc/nanoutils/curry2';
import not from '@Ravnur-Inc/nanoutils/not';
import moment from 'moment';
import { Vue, prop, setup } from 'vue-class-component';

import { $t, metadataFieldsService } from '../metadata-facade';

import Fork from './fork';

import { finder } from '@/helpers/finder';
import {
  Metadata,
  Metadata$Field,
  Metadata$FieldType,
  MetadataFormatters,
  PrepopulatedValue,
} from '@/modules/metadata/types/Metadata';
import useSecurityStore from '@/store/security';
import uidGenerator from '@/helpers/uid-generator';
import { StandardOption } from '@/types/Component';

import './metadata-list.scss';
import { Watch } from '@Ravnur-Inc/decorators';
import ReorderingList from '@/components/reordering-list/reordering-list';

type PreparedMetadata = {
  label: string;
  fieldType: Nullable<Metadata$FieldType>;
  predefinedValues: PrepopulatedValue[];
  allowFreeEntryValue: boolean;
} & OrderableMetadata;

type OrderableMetadata = { id: string; order: number } & Metadata;

const CN = 'metadata-list';

const FREE_VALUE_ID = uidGenerator();

const TYPE_KEYS = {
  [Metadata$FieldType.STRING]: 'metadata-field-type__string',
  [Metadata$FieldType.INTEGER]: 'metadata-field-type__integer',
  [Metadata$FieldType.DECIMAL]: 'metadata-field-type__decimal',
  [Metadata$FieldType.DATE]: 'metadata-field-type__date',
};

export const metadataConverter = _curry2(convert);
function convert(
  fields: Dictionary<Metadata$Field>,
  m: Unsaved<OrderableMetadata>
): Unsaved<PreparedMetadata> {
  const field = fields[m.fieldId];
  const label = field ? field.name : '';
  const fieldType = field ? field.type : null;
  const predefinedValues = field ? field.predefinedValues : [];
  const allowFreeEntryValue = field ? field.allowFreeEntryValue : false;
  const res = { label, fieldType, predefinedValues, allowFreeEntryValue, ...m };

  Object.defineProperty(res, 'value', {
    set(value: string | Date) {
      if (value instanceof Date) {
        m.value = moment(value).format('YYYY-MM-DD');
      } else {
        m.value = value;
      }
    },
    get() {
      return `${m.value}`;
    },
  });

  return res;
}

const isSameMetadata =
  ({ fieldId }: Unsaved<Metadata>) =>
  (m: Unsaved<Metadata>) =>
    m.fieldId === fieldId;

class Props {
  metadata = prop<Array<Unsaved<Metadata>>>({ default: [] });
}

type Emits = {
  onChange: (metadata: Array<Unsaved<Metadata>>) => void;
};

export default class MetadataList extends Vue.with(Props) {
  declare $props: $Props<Props, Emits, 'metadata'>;
  private security = setup(() => useSecurityStore());

  private q = '';
  private freeInputFieldIds: string[] = [];

  get fields(): Dictionary<Metadata$Field> {
    return metadataFieldsService.cache;
  }

  get list(): Array<Unsaved<PreparedMetadata>> {
    const { metadata, fields } = this;
    return metadata.map((m, idx) => ({ ...m, order: idx })).map(metadataConverter(fields));
  }

  get filtered(): Array<Unsaved<PreparedMetadata>> {
    const predicate = finder<Unsaved<PreparedMetadata>>(this.q, ['label', 'value']);
    return this.list.filter(predicate);
  }

  set filtered(list: Unsaved<PreparedMetadata>[]) {
    this.$emit('change', list);
    this.$emit('update:metadata', list);
  }

  get canEditMetadata() {
    return this.security.canEditMetadata;
  }

  created() {
    metadataFieldsService.load();
  }

  @Watch('filtered', { immediate: true })
  onFilteredChanged() {
    this.filtered.forEach((i) => {
      const customValue = !i.predefinedValues.some((v) => v.value === i.value);
      const exist = this.freeInputFieldIds.includes(i.fieldId);
      const hasValue = i.value !== '';
      const hasPredefinedValues = i.predefinedValues.length > 0;

      if (hasValue && hasPredefinedValues && i.allowFreeEntryValue && customValue && !exist) {
        this.freeInputFieldIds.push(i.fieldId);
      }
    });
  }

  render() {
    return (
      <div class={CN}>
        <div class={`${CN}__reordable-head`}>
          <div>{$t('media', 'metadata-list__field')}</div>
          <div>{$t('media', 'metadata-list__type')}</div>
          <div>{$t('media', 'metadata-list__metadata-value')}</div>
          <div></div>
        </div>
        <ReorderingList
          v-slots={{ item: this.renderOrderableRow }}
          vModel={[this.filtered as any, 'list']}
        />
      </div>
    );
  }

  renderOrderableRow({ entity }: { entity: PreparedMetadata }) {
    return (
      <div class={`${CN}__reordable-row`}>
        <div>{entity.label}</div>
        <div>{entity.fieldType ? $t('media', TYPE_KEYS[entity.fieldType]) : ''}</div>
        <div>{this.renderInputComponent({ item: entity })}</div>
        <div v-show={this.canEditMetadata}>{this.renderActions({ item: entity })}</div>
      </div>
    );
  }

  renderInputComponent({ item: m }: { item: PreparedMetadata }) {
    return this.freeInputFieldIds.includes(m.fieldId) || m.predefinedValues.length === 0 ? (
      <div class={`${CN}__free-input-wrapper`}>
        {this.renderFork(m)}
        {m.predefinedValues.length > 0 && (
          <r-button
            color="grey"
            icon="close"
            mode="frameless"
            onclick={() => this.removeFreeInput(m)}
          />
        )}
      </div>
    ) : (
      this.renderChosen(m)
    );
  }

  renderChosen(m: PreparedMetadata) {
    const { canEditMetadata } = this;
    const defaultValue = m.predefinedValues.find((v) => v.isDefault);
    const options = [
      ...m.predefinedValues.map((v) => {
        if (m.fieldType === Metadata$FieldType.DATE) {
          return { id: v.value, label: moment(v.value).format('M/D/YYYY') };
        }

        return { id: v.value, label: v.value };
      }),
    ];

    if (!m.value && defaultValue && defaultValue.value) {
      m.value = defaultValue.value;
    }

    if (m.allowFreeEntryValue) {
      options.push({
        id: FREE_VALUE_ID,
        label: $t('media', 'metadata-field__free-new-value'),
      });
    }

    return (
      <Fork
        isDropdown
        class={[`${CN}__field`, m.allowFreeEntryValue ? `${CN}__with-free-entry` : '']}
        disabled={!canEditMetadata}
        dropdownOptions={options as StandardOption[]}
        forkFormatters={MetadataFormatters}
        shouldValidateValue={true}
        value={m.value}
        onInput={(value) => {
          this.onSelect(value as string, m);
        }}
      />
    );
  }

  renderFork(m: PreparedMetadata) {
    const { canEditMetadata } = this;

    return (
      <Fork
        class={`${CN}__field`}
        disabled={!canEditMetadata}
        forkFormatters={MetadataFormatters}
        shouldValidateValue={true}
        type={m.fieldType}
        value={m.value}
        onInput={(v) => this.updateValue(v as any, m.id)}
      />
    );
  }

  private renderActions({ item: m }: { item: PreparedMetadata }) {
    const title = $t('media', 'action__delete');
    return (
      <r-button
        color="grey"
        icon="delete"
        mode="borderless"
        onclick={() => this.remove(m)}
        title={title}
      />
    );
  }

  onSelect(selected: string, item: PreparedMetadata) {
    let value = '';

    if (selected === FREE_VALUE_ID) {
      this.freeInputFieldIds.push(item.fieldId);
    } else {
      value = selected;
    }

    this.updateValue(value, item.id);
  }

  updateValue(value: string | number | Date, id: string) {
    this.filtered = this.list.map((i) => {
      return i.id === id ? { ...i, value } : i;
    });
  }

  removeFreeInput(item: PreparedMetadata) {
    const idx = this.freeInputFieldIds.indexOf(item.fieldId);

    if (idx > -1) {
      this.freeInputFieldIds.splice(idx, 1);
      this.$nextTick(() => {
        item.value = '';
      });
    }
  }

  remove(metadata: PreparedMetadata) {
    this.filtered = this.filtered.filter(not(isSameMetadata(metadata)));
  }
}
